import React, {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import clsx from 'clsx';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  label?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'search';
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  icon?: React.ReactNode;
  onIconClick?: () => void;
  // Props pour React Hook Form
  register?: any;
  // Propriétés supplémentaires pour les tests
  name?: string;
  success?: boolean;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  readonly?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  spellCheck?: boolean;
  step?: string;
  min?: number;
  max?: number;
  style?: React.CSSProperties;
  'data-testid'?: string;
  'data-cy'?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  helpText?: string;
  showCharacterCount?: boolean;
  loading?: boolean;
  clearable?: boolean;
  passwordToggle?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input({
  label,
  placeholder,
  type = 'text',
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  className = '',
  icon,
  onIconClick,
  register,
  success = false,
  size = 'md',
  fullWidth = true,
  readonly = false,
  loading = false,
  clearable = false,
  passwordToggle = false,
  showCharacterCount = false,
  maxLength,
  helpText,
  leftIcon,
  rightIcon,
  ...rest
}, ref) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value || '');
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };
  
  const inputType = type === 'password' && passwordToggle && showPassword ? 'text' : type;
  
  return (
    <div className={clsx(fullWidth ? 'w-full' : '', className)}>
      {label && (
        <label className="block text-sm font-medium text-eg-brun mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {leftIcon && (
          <span className="absolute left-3 inline-flex items-center" data-testid="left-icon">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          type={inputType}
          value={inputValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            if (onChange) onChange(newValue);
          }}
          placeholder={placeholder}
          disabled={disabled || loading}
          required={required}
          readOnly={readonly}
          maxLength={maxLength}
          className={clsx(
            'w-full border rounded-md transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'disabled:bg-eg-papier disabled:cursor-not-allowed',
            sizeClasses[size],
            leftIcon && 'pl-9',
            rightIcon && 'pr-9',
            icon && 'pr-10',
            clearable && 'pr-10',
            passwordToggle && 'pr-10',
            error 
              ? 'border-red-300 focus:ring-red-500' 
              : success
              ? 'border-green-300 focus:ring-green-500'
              : 'border-[#DDE2DD] focus:ring-primary-500'
          )}
          {...register}
          {...rest}
        />
        {icon && (
          <button
            type="button"
            onClick={onIconClick}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-eg-estompe hover:text-eg-gris transition-colors duration-200"
          >
            {icon}
          </button>
        )}
        {rightIcon && (
          <span className="absolute right-3 inline-flex items-center" data-testid="right-icon">
            {rightIcon}
          </span>
        )}
        {clearable && inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              if (onChange) onChange('');
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-eg-estompe hover:text-eg-gris transition-colors duration-200"
            aria-label="Clear input"
          >
            ✕
          </button>
        )}
        {passwordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-eg-estompe hover:text-eg-gris transition-colors duration-200"
            aria-label="Toggle password visibility"
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-eg-accent">{error}</p>
      )}
      {helpText && (
        <p className="mt-1 text-sm text-eg-gris">{helpText}</p>
      )}
      {showCharacterCount && maxLength && (
        <p className="mt-1 text-sm text-eg-gris text-right">
          {inputValue.length}/{maxLength}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
